/**
 * Hero — playful clay. Left: value prop + dual CTA (Book a demo / browse as an
 * owner). Right: a self-contained "app preview" built from clay primitives (no
 * raster assets) with floating service tiles for a toy-like, friendly feel.
 */
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarCheck,
  Heart,
  PawPrint,
  Scissors,
  Sparkles,
  Star,
  Stethoscope,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export function LandingHero() {
  const navigate = useNavigate();

  return (
    <section id="top" className="relative overflow-hidden">
      {/* Soft floating colour blobs */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-24 top-4 h-72 w-72 rounded-full bg-accent/25 blur-3xl animate-blob" />
        <div className="absolute right-0 top-32 h-80 w-80 rounded-full bg-primary/20 blur-3xl animate-blob [animation-delay:-6s]" />
        <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-info/15 blur-3xl animate-blob [animation-delay:-10s]" />
      </div>

      <div className="mx-auto grid w-full max-w-6xl items-center gap-12 px-5 py-16 md:grid-cols-2 md:py-24">
        {/* Copy */}
        <div className="flex flex-col items-start gap-6 duration-500 animate-in fade-in slide-in-from-bottom-4">
          <span className="inline-flex items-center gap-2 rounded-full border-2 border-brand-outline-variant/30 bg-card px-4 py-1.5 text-sm font-semibold text-foreground shadow-clay-sm">
            <Sparkles className="h-4 w-4 text-primary" />
            Every pet service, one happy place
          </span>

          <h1 className="font-heading text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl md:text-6xl">
            Care for your pet,{' '}
            <span className="bg-gradient-to-r from-[#FF6B9D] to-[#FF4D8A] bg-clip-text text-transparent">
              booked in a tail-wag.
            </span>
          </h1>

          <p className="max-w-md text-lg text-muted-foreground">
            Vets, walkers, trainers, grooming, cafés and more — discover trusted local pros, book in
            seconds, and keep every pet's care in one cuddly little app.
          </p>

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              variant="gradient"
              size="lg"
              className="text-base"
              onClick={() => navigate('/choose')}
              data-testid="hero-get-started"
            >
              <PawPrint className="h-5 w-5" />
              Get started
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="text-base"
              onClick={() => navigate('/user/login')}
              data-testid="hero-browse-owner"
            >
              Browse as a pet owner
            </Button>
          </div>

          {/* Trust row */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 pt-2">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {['#FF6B9D', '#FF4D8A', '#2563EB', '#2E7D32'].map((c) => (
                  <span
                    key={c}
                    className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-card text-white shadow-clay-sm"
                    style={{ backgroundColor: c }}
                  >
                    <PawPrint className="h-4 w-4" />
                  </span>
                ))}
              </div>
              <span className="text-sm font-semibold text-foreground">1,200+ happy pet parents</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="flex">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-4 w-4 fill-warning text-warning" />
                ))}
              </div>
              <span className="text-sm font-semibold text-foreground">4.9 average rating</span>
            </div>
          </div>
        </div>

        {/* App preview mock */}
        <div className="relative mx-auto w-full max-w-sm duration-700 animate-in fade-in slide-in-from-bottom-6">
          {/* Floating service tiles */}
          <FloatTile
            className="-left-6 top-6 animate-float"
            icon={<Stethoscope className="h-5 w-5" />}
            label="Vet visit"
            tint="bg-info text-info-foreground"
          />
          <FloatTile
            className="-right-4 top-24 animate-float [animation-delay:-2s]"
            icon={<Scissors className="h-5 w-5" />}
            label="Grooming"
            tint="bg-primary text-primary-foreground"
          />
          <FloatTile
            className="-left-2 bottom-6 animate-float [animation-delay:-4s]"
            icon={<Heart className="h-5 w-5" />}
            label="Adoption"
            tint="bg-success text-success-foreground"
          />

          {/* The "card" */}
          <div className="rounded-card border-2 border-brand-outline-variant/20 bg-card p-5 shadow-clay-lg">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-bold text-secondary-foreground">
                <span className="h-2 w-2 rounded-full bg-success" />
                Booking confirmed
              </span>
              <PawPrint className="h-5 w-5 text-primary" />
            </div>

            <div className="mt-4 flex items-center gap-3">
              <span className="flex h-14 w-14 items-center justify-center rounded-button accent-gradient text-2xl text-white shadow-clay-sm">
                <PawPrint className="h-7 w-7" />
              </span>
              <div>
                <p className="font-heading text-lg font-bold leading-tight">Bella's spa day</p>
                <p className="text-sm text-muted-foreground">Full groom · Golden Retriever</p>
              </div>
            </div>

            <div className="mt-4 space-y-2.5">
              <PreviewRow label="With" value="Paws & Bubbles" />
              <PreviewRow label="When" value="Sat, 10:30 AM" />
              <PreviewRow label="Where" value="2.1 km away" />
            </div>

            <div className="mt-5 flex items-center justify-between rounded-button bg-secondary px-4 py-3">
              <span className="text-sm font-semibold text-muted-foreground">Total</span>
              <span className="font-heading text-xl font-bold tabular-nums">₹1,499</span>
            </div>

            <div className="mt-4 flex w-full items-center justify-center gap-2 rounded-button accent-gradient px-4 py-3 font-semibold text-white shadow-clay-sm">
              <CalendarCheck className="h-5 w-5" />
              Rebook in one tap
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FloatTile({
  className,
  icon,
  label,
  tint,
}: {
  className: string;
  icon: ReactNode;
  label: string;
  tint: string;
}) {
  return (
    <div
      aria-hidden
      className={`absolute z-10 hidden items-center gap-2 rounded-button border-2 border-brand-outline-variant/20 bg-card px-3 py-2 shadow-clay sm:flex ${className}`}
    >
      <span className={`flex h-8 w-8 items-center justify-center rounded-md ${tint}`}>{icon}</span>
      <span className="pr-1 text-sm font-bold">{label}</span>
    </div>
  );
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-foreground">{value}</span>
    </div>
  );
}
